import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import {
  MesDataService,
  MesSerialByBlockScheduleResult,
  MesSerialSyncRecord,
  MesSerialSyncHistoryRecord
} from '../../../services/mes-data.service';

interface MesTemplateGroup {
  templateName: string;
  collapsed: boolean;
  sections: MesSectionGroup[];
}

interface MesSectionGroup {
  sectionName: string;
  records: MesSerialSyncRecord[];
}

@Component({
  selector: 'app-mes-serial-numbers-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, TabsModule, TooltipModule, ToastModule],
  templateUrl: './mes-serial-numbers-modal.component.html',
  styleUrls: ['./mes-serial-numbers-modal.component.css'],
  providers: [MessageService]
})
export class MesSerialNumbersModalComponent implements OnChanges {

  @Input() visible = false;
  @Input() blockScheduleId: number = 0;
  @Output() visibleChange = new EventEmitter<boolean>();

  loading = false;
  serialResults: MesSerialByBlockScheduleResult[] = [];
  groupedBySerial: { serialNumber: string; templateGroups: MesTemplateGroup[] }[] = [];

  // Edit state
  editingId: number | null = null;
  editValue: string = '';
  saving = false;

  // History dialog state
  showHistoryDialog = false;
  historyLoading = false;
  historyRecords: MesSerialSyncHistoryRecord[] = [];
  historyAttributeName = '';

  constructor(
    private mesDataService: MesDataService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true && this.blockScheduleId) {
      this.loadData();
    }
    if (changes['visible']?.currentValue === false) {
      this.resetState();
    }
  }

  private loadData(): void {
    this.loading = true;
    this.serialResults = [];
    this.groupedBySerial = [];

    this.mesDataService.getByBlockScheduleId(this.blockScheduleId).subscribe({
      next: (results) => {
        this.serialResults = results;
        this.groupedBySerial = results.map(r => ({
          serialNumber: r.serialNumber,
          templateGroups: this.buildTemplateGroups(r.records)
        }));
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load serial number data.' });
        this.cdr.markForCheck();
      }
    });
  }

  private buildTemplateGroups(records: MesSerialSyncRecord[]): MesTemplateGroup[] {
    const templateMap = new Map<string, Map<string, MesSerialSyncRecord[]>>();

    for (const record of records) {
      if (!templateMap.has(record.templateName)) {
        templateMap.set(record.templateName, new Map());
      }
      const sectionMap = templateMap.get(record.templateName)!;
      if (!sectionMap.has(record.sectionName)) {
        sectionMap.set(record.sectionName, []);
      }
      sectionMap.get(record.sectionName)!.push(record);
    }

    const groups: MesTemplateGroup[] = [];
    templateMap.forEach((sectionMap, templateName) => {
      const sections: MesSectionGroup[] = [];
      sectionMap.forEach((recs, sectionName) => {
        sections.push({ sectionName, records: recs });
      });
      groups.push({ templateName, collapsed: false, sections });
    });

    return groups;
  }

  toggleTemplate(group: MesTemplateGroup): void {
    group.collapsed = !group.collapsed;
  }

  startEdit(record: MesSerialSyncRecord): void {
    this.editingId = record.id;
    this.editValue = record.attributeValue ?? '';
  }

  cancelEdit(): void {
    this.editingId = null;
    this.editValue = '';
  }

  saveEdit(record: MesSerialSyncRecord): void {
    if (this.saving) return;
    this.saving = true;

    this.mesDataService.updateAttributeValue({
      serialNumber: record.serialNumber,
      templateName: record.templateName,
      sectionName: record.sectionName,
      attributeName: record.attributeName,
      newValue: this.editValue || null
    }).subscribe({
      next: () => {
        record.attributeValue = this.editValue || null;
        this.editingId = null;
        this.editValue = '';
        this.saving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Value updated successfully.' });
        this.cdr.markForCheck();
      },
      error: () => {
        this.saving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save value.' });
        this.cdr.markForCheck();
      }
    });
  }

  openHistory(record: MesSerialSyncRecord): void {
    this.historyAttributeName = record.attributeName;
    this.historyLoading = true;
    this.historyRecords = [];
    this.showHistoryDialog = true;

    this.mesDataService.getHistory(record.id).subscribe({
      next: (history) => {
        this.historyRecords = history;
        this.historyLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.historyLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load history.' });
        this.cdr.markForCheck();
      }
    });
  }

  closeHistory(): void {
    this.showHistoryDialog = false;
    this.historyRecords = [];
  }

  close(): void {
    this.resetState();
    this.visibleChange.emit(false);
  }

  private resetState(): void {
    this.editingId = null;
    this.editValue = '';
    this.showHistoryDialog = false;
    this.historyRecords = [];
  }
}
